// Fills the booking journey's delegate forms with made-up people, on demo boxes.
//
// The forms are cloned from a <template> by event-booking-service.ts once a ticket
// quantity is chosen, so there is nothing on the server to pre-fill - by the time
// they exist the page has long been rendered. The server instead hands over a pool
// of people in data-demo-delegates, and each form that appears takes the next one.
//
// Only ever fills blanks, so anything typed during a demo survives.

interface DemoDelegate {
  Salutation: string;
  FirstName: string;
  LastName: string;
  Email: string;
  DietaryRequirements: string;
  AccessRequirements: string;
}

export function initDelegatePrefill() {
  const source = document.querySelector<HTMLElement>('[data-demo-delegates]');
  // The course booking step is built the same way, with its own container.
  const delegates = document.getElementById('event-delegates')
    ?? document.getElementById('course-delegates');

  if (!source || !delegates) return;

  let pool: DemoDelegate[] = [];

  try {
    pool = JSON.parse(source.dataset.demoDelegates ?? '[]');
  } catch {
    return;
  }

  if (pool.length === 0) return;

  let next = 0;

  function fill(form: HTMLElement) {
    // A form is only filled once, however many times the list is rebuilt.
    if (form.dataset.demoFilled === 'true') return;
    form.dataset.demoFilled = 'true';

    const person = pool[next % pool.length];
    next += 1;

    const set = (suffix: string, value: string) => {
      if (!value) return;

      const field = form.querySelector<HTMLInputElement | HTMLSelectElement>(
        `[name$=".${suffix}"]`
      );

      if (!field || field.value) return;

      field.value = value;

      // The booking script watches these to decide whether a delegate is complete.
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    };

    set('Salutation', person.Salutation);
    set('FirstName', person.FirstName);
    set('LastName', person.LastName);
    set('EmailAddress', person.Email);
    set('DietaryRequirements', person.DietaryRequirements);
    set('AccessRequirements', person.AccessRequirements);

    // The booking script validates a delegate on the email field losing focus, and
    // only then flips its Validated flag and enables 'Continue to payment'. Filling
    // the values is not enough on its own - without this the button stays disabled.
    const email = form.querySelector<HTMLInputElement>('[name$=".EmailAddress"]');
    email?.dispatchEvent(new Event('blur'));
  }

  const fillAll = () =>
    delegates.querySelectorAll<HTMLElement>('.delegate-form').forEach(fill);

  fillAll();

  // The forms come and go as the quantity changes, so watch rather than run once.
  new MutationObserver(fillAll).observe(delegates, { childList: true, subtree: true });
}

// Finishes the checkout after the input cache has had its say, on demo boxes.
//
// The server fills what the contact record leaves blank, but the cached-input
// module restores whatever was last typed on this machine - which puts the terms
// box back to unticked. Validation then fails on TermsAllAgreed and "Pay now"
// returns silently, with no spinner and no message, so the booking simply cannot
// be submitted. This runs after that restore and puts the blanks back.
export function initCheckoutPrefill() {
  if (!document.querySelector('[data-demo-checkout]')) return;

  const finish = () => {
    const terms = document.querySelector<HTMLInputElement>('[name="TermsAllAgreed"]');

    if (terms && !terms.checked) {
      terms.checked = true;
      terms.classList.remove('input-validation-error');
      terms.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const title = document.querySelector<HTMLSelectElement>('[name$="Salutation"]');

    if (title && !title.value) {
      // The first option is the "Please select" placeholder, with an empty value.
      // Mr where offered, rather than whatever sorts first.
      const opts = [...title.options].filter(o => o.value);
      const real = opts.find(o => /^mr$/i.test(o.text.trim())) ?? opts[0];
      if (real) {
        title.value = real.value;
        title.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  finish();

  // The cache restores asynchronously, so once more after it has settled.
  window.setTimeout(finish, 600);
}
